import { groupBy, omit } from 'lodash-es';

import {
  ALLOWED_ARNM_AGENT_ROLES,
  ARNM_REASONS_MAP,
  BOARDS_ID,
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
} from '@constants';
import { formatDate, sortArray } from '@utils';

import {
  ARNM_ACCIDENTS_TASK_STATUSES_NAMES,
  ARNM_REASONS_ACCIDENTS_MAP,
} from 'components/arnmAccidents/constants';
import { URLS } from 'layouts/DrawerLayout/constants';

const getValue = (data, current) => {
  return data.find((item) => item?.value === current);
};

export const handleArnmAccidentsTaskData = (
  data,
  servicePoints = [],
  assignees = [],
  stages = []
) => {
  if (!data) {
    return {};
  }
  const servicePoint = getValue(servicePoints, data.customFields.servicePoint);
  const assignee = getValue(assignees, data.assignee);
  const status = getValue(stages, data.status);

  const task = {
    ...data,
    assignee,
    status,
    customFields: {
      ...data.customFields,
      servicePoint,
      reason: ARNM_REASONS_MAP[data.customFields.reason] || '-',
      totalChargeClientAmount:
        data.customFields.totalChargeClientAmount?.number || null,
      totalOfferAmount: data.customFields.totalOfferAmount?.number || null,
      totalInvoiceCost: data.customFields.totalInvoiceCost?.number || null,
      totalInsuranceAmount:
        data.customFields.totalInsuranceAmount?.number || null,
      workOrder: data.customFields.workOrder?.map?.((item) => ({
        ...item,
        chargeClientAmount: item.chargeClientAmount?.number || null,
        offerAmount: item.offerAmount?.number || null,
      })),
    },
    changelog: data?.changelog?.reverse(),
    comments: sortArray(data?.comments, 'createdAt', 'desc')?.map((entry) => {
      return {
        id: entry?.id,
        date: entry?.createdAt
          ? formatDate(entry?.createdAt, 'dd MMMM yyyy')
          : '-',
        time: entry?.createdAt ? formatDate(entry?.createdAt, 'HH:mm') : '-',
        name: entry?.userFullName || entry?.user || '-',
        comment: entry?.comment,
        edit: entry?.editedAt
          ? formatDate(entry?.editedAt, 'dd MMMM yyyy HH:mm')
          : '',
      };
    }),
    documents: data?.documents,
  };
  return task;
};

export const handleVehicleData = (data = {}) => {
  const properties = [];

  if (data.properties?.fuelType) {
    properties.push(data.properties.fuelType);
  }
  if (data.properties?.transmission) {
    properties.push(data.properties.transmission);
  }
  if (data.properties?.color) {
    properties.push(data.properties.color);
  }
  if (data.year) {
    properties.push(data.year);
  }

  return {
    internalId: data.internalId,
    id: data.id,
    title: `${data.manufacturer}, ${data.model}`,
    plate: data.plate,
    image: data?.images?.[0]?.url,
    properties,
    vin: data.vin,
    procurement: data.procurement,
  };
};

export const handleArnmAssigneeData = (data = []) =>
  data?.filter((item) => ALLOWED_ARNM_AGENT_ROLES.has(item?.role));

export const handleInsurancesData = (data = []) =>
  data
    ?.sort((a, b) => a.name.localeCompare(b.name))
    ?.map((item) => ({
      value: item.id,
      text: item.name,
    }));

export const getArnmAccidentTaskPayload = (values) => {
  Object.keys(values)?.forEach((key) => {
    if (values[key] === '') {
      values[key] = null;
    }
  });

  const cleanedValues = omit(values, [
    'assignee',
    'documents',
    'tags',
    'status',
    'updatedAt',
    'updatedBy',
    'changelog',
    'images',
    'parent',
    'name',
    'createdAt',
    'comments',
    'createdBy',
    'servicePoint',
    'subscriptionId',
    'customFields.comments',
    'customFields.vehicleDescription',
    'customFields.vehicleImage',
    'customFields.vehicleOwner',
    'customFields.vehiclePlate',
    'customFields.dateEnteredWaitingReceipt',
    'customFields.subscriptionId',
    'customFields.totalChargeClientAmount',
    'customFields.totalCounterOfferAmount',
    'customFields.workOrderId',
  ]);

  return {
    assignee: values.assignee?.value,
    customFields: {
      ...cleanedValues.customFields,
      servicePoint: values.customFields.servicePoint?.value,
      expectedOutFromServicePoint:
        values.customFields.expectedOutFromServicePoint || null,
    },
  };
};

export const handleArnmAccidentsData = (data, assignees) => {
  if (!data?.length) {
    return [];
  }
  const assigneesMap = assignees.reduce((acc, cur) => {
    acc[cur.value] = cur;
    return acc;
  }, {});
  const tasks = [];
  data?.forEach((item) => {
    const assignee = assigneesMap[item.assignee] || {};

    const vehicleDescription = item?.customFields?.vehicleDescription || '';

    const vehicleReason = item?.customFields?.reason || '';

    const description = `${vehicleDescription} - ${vehicleReason}`;

    const newTasks = {
      ...item,
      href: `${URLS.arnmAccidents}/${item?.id}`,
      plate: item.customFields?.vehiclePlate || '',
      status: item?.status,
      description,
      assignee,
      date: item.createdAt ? formatDate(item.createdAt, 'dd/MM/yyyy') : null,
      createdAt: item?.createdAt
        ? formatDate(item?.createdAt, 'dd/MM/yyyy')
        : '-',
      // TODO: handle custom fields
      id: item?.id,
      linkColor: item?.customFields?.doWeNeedATempCar && 'red',
      servicePoint: item?.customFields?.servicePoint || '-',
      reason: ARNM_REASONS_ACCIDENTS_MAP[item?.customFields?.reason] || '-',
      outDateFromServicePoint: item?.customFields?.outDateFromServicePoint
        ? formatDate(item?.customFields?.outDateFromServicePoint, 'dd/MM/yyyy')
        : '-',
      dateToServicePoint: item?.customFields?.dateToServicePoint
        ? formatDate(item?.customFields?.dateToServicePoint, 'dd/MM/yyyy')
        : '-',
      expectedOutFromServicePoint: item?.customFields
        ?.expectedOutFromServicePoint
        ? formatDate(
            item?.customFields?.expectedOutFromServicePoint,
            'dd/MM/yyyy'
          )
        : '-',
      title: item?.customFields?.vehiclePlate || '',
      vehicleDescription: item?.customFields?.vehicleDescription || '',
    };
    tasks.push(newTasks);
  });

  return tasks;
};

export const getArnmAccidentsColumns = (tasks) => {
  const order = [0, 1, 2, 13, 14, 16, 17, 18, 19, 20, 21, 3, 12];
  const groupedByStatus = groupBy(tasks, 'status');

  const columns = order.reduce((acc, cur) => {
    acc.push({
      id: cur,
      title: ARNM_ACCIDENTS_TASK_STATUSES_NAMES[cur].label,
      cards: groupedByStatus[cur] || [],
      styles: ARNM_ACCIDENTS_TASK_STATUSES_NAMES[cur].styles,
      value: true,
      label: ARNM_ACCIDENTS_TASK_STATUSES_NAMES[cur].label,
      key: cur,
    });
    return acc;
  }, []);

  return columns;
};

export const getArnmAccidentsPayload = (
  payload = {},
  modalFiltersRules = null
) => {
  const fields =
    omit(payload, [
      'page',
      'pageSize',
      'order',
      'orderBy',
      'boardType',
      'query',
    ]) || {};
  const fieldsMap = {
    reason: 'customFields.reason',
    servicePoint: 'customFields.servicePoint',
    stage: 'status',
  };

  const multyFilterStringFields = ['reason', 'servicePoint'];
  const multyFilterNumberFields = [
    'stage',
    'reservationType',
    'procurementStageId',
  ];
  const defaultRule = {
    field: 'boardId',
    operator: FILTER_OPERATOR_ENUMS['='],
    type: FILTER_RULE_ENUMS.ComparableRuleType,
    value: BOARDS_ID.ARM_ACCIDENTS,
  };

  const getValueField = ({ field }) => {
    if (field === 'assignee') {
      return payload[field] === 'none' ? '' : payload[field];
    }
    if (multyFilterStringFields.includes(field)) {
      return payload[field]?.split(',');
    }
    if (multyFilterNumberFields.includes(field)) {
      return payload[field]?.split(',').map((item) => Number(item));
    }
    return payload[field];
  };
  const getType = ({ field }) => {
    if (
      multyFilterStringFields.includes(field) ||
      multyFilterNumberFields.includes(field)
    ) {
      return FILTER_RULE_ENUMS.IsAnyOfRuleType;
    }
    return FILTER_RULE_ENUMS.ComparableRuleType;
  };
  const rules = [...Object.keys(fields)].map((field) => ({
    field: fieldsMap[field] || field,
    operator: FILTER_OPERATOR_ENUMS['='],
    type: getType({ field }),
    value: getValueField({ field }),
  }));
  rules.push(...(modalFiltersRules?.value || []));
  rules.push(defaultRule);

  return {
    type: 0,
    field: null,
    operator: null,
    value: rules,
  };
};

export const normalizeOrderBy = (orderBy) => {
  switch (orderBy) {
    case 'dateToServicePoint':
      return `customFields.${orderBy}`;
    default:
      return orderBy;
  }
};
